// src/pages/TrackCreation/PhotosScreen.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { PhotosScreenProps } from "../../../../types/screensTypes";
import { FileInput } from "../../../../components/common/FileInput";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { uploadFileToS3Complete } from "../../../../api/s3";
import Button from "../../../../components/ui/Button";
import ImageList from "../../../../components/images/ImageList";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../../../utils/image";
import ImageSpecsBadges from "../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../hooks/useImageValidation";
import stepStyles from "../createStep.module.css";
import styles from "./Photos.module.css";

const PhotosScreen: React.FC<PhotosScreenProps> = ({ navigateToNext }) => {
    const { trackData, updateTrackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const photos = trackData.photos || [];
    const imageValidation = useImageValidation({
        type: "productPhotos",
        imageUrl: photos[photos.length - 1],
    });

    const handleFileSelected = async (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await imageValidation.validateImage(file);

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true,
                    isFolder: false,
                },
                token,
            )
                .then((result: string) => {
                    const updatedPhotos = [...(trackData.photos || []), result];
                    updateTrackData("photos", updatedPhotos);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const setPictures = (pictures: string[]): void => {
        updateTrackData("photos", pictures);
        if (pictures.length === 0) {
            imageValidation.clearValidation();
        }
    };

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step}`}>
            <TitleSubtitle
                title={t("addPhotosToYourAudioTour")}
                subtitle={t("imagesHelpVisitorsVisualizeJoiningYourActivity")}
                layout="photos"
            />

            <div className={`${stepStyles.stepDescription} ${styles.photosSec} ${styles.photosSecFirst}`}>
                <h2>{t("requirements")}</h2>
                <ImageSpecsBadges spec="productPhotos" warnings={imageValidation.warnings} />
                <ul className={`no-dots ${styles.photosWarn}`}>
                    <li>{t("noPhotographerWatermarksLogosOrReadableLicensePlates")}</li>
                </ul>
            </div>

            <div className={`${stepStyles.stepDescription} ${styles.photosSec}`}>
                <div className={`${stepStyles.fileUpload} ${styles.photosUpload}`}>
                    <h2>{t("uploadYourPhotos")}</h2>
                    <p>{t("dragYourPhotosIntoTheAreaBelowOrSelectUploadPhotos")}</p>
                    <FileInput
                        onFileSelected={handleFileSelected}
                        showButton={true}
                        acceptedFileTypes={IMAGE_FORMAT}
                        maxSize={IMAGE_MAX_SIZE_200_KB}
                        maxSizeLabel={t("imageMaxSize200kb")}
                    />
                </div>
            </div>

            {trackData && trackData.photos && (
                <div className="photo-gallery">
                    <ImageList pictures={trackData.photos} setPictures={setPictures} />
                </div>
            )}

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("continue")}
                    onClick={async () => {
                        await navigateToNext();
                    }}
                    disabled={photos.length === 0}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>
        </div>
    );
};

export default PhotosScreen;
